<template>
  <div
    class="bg-top-navigation fixed top-0 right-0 left-0 z-20 flex h-16 items-center justify-between space-x-4 border-b px-4 lg:px-8"
  >
    <div class="flex min-w-0 items-center gap-1.5 md:gap-4">
      <SidebarTrigger class="md:hidden" />
      <RouterLink :to="{ name: 'overview' }" class="hidden items-center md:flex">
        <LogoGradient class="h-7 w-7" />
      </RouterLink>
      <div class="text-muted-foreground/30 hidden text-2xl sm:block">/</div>
      <div class="flex min-w-0 items-center">
        <Popover v-model:open="teamMenuOpen">
          <PopoverTrigger as-child>
            <Button
              variant="outline"
              role="combobox"
              :aria-expanded="teamMenuOpen"
              class="h-auto min-w-0 gap-1.5 rounded-full py-1 pr-2 pl-1 shadow-none"
            >
              <div
                v-if="team"
                class="flex size-5 shrink-0 items-center justify-center rounded-full text-xs"
                :style="{ backgroundColor: team.color }"
                :class="[isColorLight(team.color) ? 'text-black' : 'text-white']"
              >
                {{ team.name[0]?.toUpperCase() }}
              </div>
              <Avatar v-else class="size-6 shrink-0">
                <AvatarImage :src="profile?.gravatar_url ?? ''" alt="Profile picture" />
                <AvatarFallback>{{ profile?.first_name?.[0]?.toUpperCase() }}</AvatarFallback>
              </Avatar>
              <span class="max-w-40 min-w-0 truncate font-medium">
                {{ team ? team.name : `${profile?.first_name} ${profile?.last_name}` }}
              </span>
              <ChevronsUpDownIcon class="size-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" class="w-[280px] p-0">
            <Command highlight-on-hover>
              <CommandInput class="h-9" placeholder="Search team..." />
              <CommandList>
                <CommandEmpty>No teams found.</CommandEmpty>
                <div
                  v-if="teams && teams.teams.length === 0"
                  class="text-muted-foreground flex flex-col items-center gap-2 px-4 py-6 text-center text-sm"
                >
                  <UsersIcon class="h-5 w-5" />
                  <div class="flex flex-col gap-1">
                    <p class="text-foreground font-medium">No teams yet</p>
                    <p class="text-xs">Create a team to collaborate with others.</p>
                  </div>
                </div>
                <CommandGroup v-if="teams && teams.teams.length > 0" heading="Teams">
                  <CommandItem
                    v-for="item in teams?.teams"
                    :key="item.uuid"
                    :value="item.uuid"
                    class="group flex items-center justify-between gap-4"
                    @select="
                      () => {
                        handleTeamSelect(item);
                        teamMenuOpen = false;
                      }
                    "
                  >
                    <div class="flex min-w-0 items-center gap-2">
                      <div
                        class="flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs"
                        :style="{ backgroundColor: item.color }"
                        :class="[isColorLight(item.color) ? 'text-black' : 'text-white']"
                      >
                        {{ item.name[0]?.toUpperCase() }}
                      </div>
                      <div class="flex min-w-0 items-center gap-2">
                        <div class="truncate font-normal">{{ item.name }}</div>
                        <Badge
                          v-if="isBillingEnabled && !item.is_billing_ready"
                          variant="outline"
                          class="shrink-0 border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-600 dark:text-amber-400"
                        >
                          Action required
                        </Badge>
                      </div>
                    </div>
                    <div class="flex items-center gap-1.5" v-if="currentTeamUuid === item.uuid">
                      <Check
                        :class="
                          cn(
                            'ml-auto h-4 w-4',
                            currentTeamUuid === item.uuid ? 'opacity-100' : 'opacity-0',
                          )
                        "
                      />
                      <RouterLink
                        :to="{ name: 'team', params: { uuid: item.uuid } }"
                        :class="
                          cn(
                            buttonVariants({ size: 'icon', variant: 'outline' }),
                            'hidden h-5 w-5 group-hover:inline-flex',
                          )
                        "
                        @click="teamMenuOpen = false"
                      >
                        <Settings2Icon class="size-3" />
                      </RouterLink>
                    </div>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator v-if="team" />
                <CommandGroup v-if="team" heading="Personal account">
                  <CommandItem
                    class="group flex items-center justify-between"
                    :value="{ name: `${profile?.first_name} ${profile?.last_name}` }"
                    @select.prevent="
                      () => {
                        handlePersonalAccountSelect();
                        teamMenuOpen = false;
                      }
                    "
                  >
                    <div class="flex items-center gap-0.5">
                      <Avatar class="mr-2 size-6 shrink-0">
                        <AvatarImage :src="profile?.gravatar_url ?? ''" alt="Profile picture" />
                        <AvatarFallback>{{ profile?.first_name?.[0]?.toUpperCase() }}</AvatarFallback>
                      </Avatar>
                      {{ profile?.first_name }} {{ profile?.last_name }}
                    </div>
                    <Check :class="cn('ml-auto h-4 w-4', !team ? 'opacity-100' : 'opacity-0')" />
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </CommandList>
            </Command>
            <div class="p-1">
              <RouterLink
                class="hover:bg-muted flex items-center rounded px-2 py-1"
                :to="{ name: 'profile' }"
                @click="teamMenuOpen = false"
              >
                <div class="bg-muted mr-2 flex h-7 w-7 items-center justify-center rounded border">
                  <UserIcon class="h-4 w-4" />
                </div>
                <span>Profile settings</span>
              </RouterLink>
              <RouterLink
                class="hover:bg-muted flex items-center rounded px-2 py-1"
                :to="{ name: 'team/new' }"
                @click="teamMenuOpen = false"
              >
                <div class="bg-muted mr-2 flex h-7 w-7 items-center justify-center rounded border">
                  <UsersIcon class="h-4 w-4" />
                </div>
                <span>Create team</span>
              </RouterLink>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
    <div class="flex shrink-0 items-center space-x-2 md:space-x-4">
      <DeployMenu />

      <Tooltip>
        <TooltipTrigger>
          <Button variant="ghost" size="icon" @click="notificationsSheetOpen = true">
            <div class="relative">
              <InboxIcon :size="20" />
              <Status
                v-if="notificationsCounter && notificationsCounter > 0"
                class="-top-1"
                status="inactive"
                size="sm"
              />
            </div>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Notifications</TooltipContent>
      </Tooltip>

      <TopUserMenu />

      <NotificationsSheet v-model:open="notificationsSheetOpen" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Check,
  ChevronsUpDownIcon,
  InboxIcon,
  Settings2Icon,
  UserIcon,
  UsersIcon,
} from '@lucide/vue';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { useNotifications } from '@/entities/notification';
import { profileQueryOptions } from '@/entities/profile';
import { type Team, teamQueryOptions, teamsQueryOptions } from '@/entities/team';
import { IS_BILLING_ENABLED } from '@/shared/config';
import { useGlobalLoader } from '@/shared/hooks';
import { useTeamCookie } from '@/shared/hooks/use-team-cookie';
import { useTeamSwitch } from '@/shared/hooks/use-team-switch';
import LogoGradient from '@/shared/icons/LogoGradient.vue';
import { cn, isColorLight } from '@/shared/lib/misc';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Button, buttonVariants } from '@/shared/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/shared/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { SidebarTrigger } from '@/shared/ui/sidebar';
import Status from '@/shared/ui/status.vue';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import DeployMenu from '@/widgets/deploy-menu.vue';
import TopUserMenu from '@/widgets/top-user-menu.vue';

import NotificationsSheet from './notifications-sheet.vue';

const router = useRouter();
const { data: profile } = useQuery(profileQueryOptions());
const { data: notifications } = useNotifications();
const teamMenuOpen = ref(false);
const notificationsSheetOpen = ref(false);
const { data: teams } = useQuery(teamsQueryOptions());
const { currentTeamUuid } = useTeamCookie();
const { data: team } = useQuery(teamQueryOptions(currentTeamUuid));
const { toggle: toggleGlobalLoader } = useGlobalLoader();
const { switchToPersonalAccount, switchToTeam } = useTeamSwitch();
const isBillingEnabled = IS_BILLING_ENABLED;

const notificationsCounter = computed(
  () => notifications.value?.filter((n) => !n.viewed_by_user).length,
);

async function handleTeamSelect(nextTeam: Team) {
  if (nextTeam.uuid === team.value?.uuid) return;

  toggleGlobalLoader(`Switching to ${nextTeam.name} team...`);

  await switchToTeam(nextTeam);

  if (IS_BILLING_ENABLED && !nextTeam.is_billing_ready) {
    const isOwner = nextTeam.members.some(
      (m) => m.uuid === profile.value?.uuid && m.role === 'Owner',
    );
    await router.push({ name: isOwner ? 'team/billing' : 'access-restricted' });
  } else {
    await router.push({ name: 'overview' });
  }

  toggleGlobalLoader();
}

async function handlePersonalAccountSelect() {
  toggleGlobalLoader(`Switching to personal account...`);

  await switchToPersonalAccount();

  await router.push({ name: 'overview' });

  toggleGlobalLoader();
}
</script>
